import { ref, onMounted, onBeforeUnmount, onUnmounted, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useScreenSize } from '@experience/shared';
import { useLoaderStore } from '@/shared/store/useLoaderStore.ts';

// Константы
const SCROLL_STORAGE_KEY = 'scroll_data';

// Интерфейсы
interface ScrollData {
  position: number;
  path: string;
  timestamp: number;
  scrollHeight?: number;
  clientHeight?: number;
}

interface ScrollRestorationOptions {
  enabled?: boolean;
  mobileOnly?: boolean;
  desktopOnly?: boolean;
  delay?: number;
  threshold?: number;
  useSessionStorage?: boolean;
  useCookies?: boolean;
  cookieExpires?: number;
  cookiePrefix?: string;
}

export function useExcursionScrollRestoration(options: ScrollRestorationOptions = {}) {
  // Настройки по умолчанию
  const {
    enabled = true,
    mobileOnly = false,
    desktopOnly = false,
    delay = 100,
    threshold = 300,
    useSessionStorage = true,
  } = options;

  // Зависимости
  const route = useRoute();
  const { isMobile } = useScreenSize();
  const loaderStore = useLoaderStore();

  // Состояние
  const isInitialized = ref(false);
  const isRestoring = ref(false);
  const lastScrollPosition = ref(0);
  const navigationType = ref<string>('navigate');

  // Определяем тип навигации (упрощенная версия)
  const detectNavigationType = (): string => {
    // Используем PerformanceNavigationTiming если доступен
    if ('performance' in window) {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const navEntry = navEntries[0] as PerformanceNavigationTiming;

        return navEntry.type;
      }
    }

    return 'navigate';
  };

  // Сохранение позиции скролла
  const saveScrollPosition = () => {
    if (!enabled || isRestoring.value) {
      return;
    }

    const scrollData: ScrollData = {
      position: window.scrollY,
      path: window.location.pathname,
      timestamp: Date.now(),
      scrollHeight: document.documentElement.scrollHeight,
      clientHeight: document.documentElement.clientHeight,
    };

    // Сохраняем в sessionStorage
    if (useSessionStorage) {
      try {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(scrollData));
      } catch (error) {
        console.warn('Failed to save scroll position to sessionStorage:', error);
      }
    }

    lastScrollPosition.value = scrollData.position;
  };

  // Получение сохраненной позиции
  const getSavedScrollPosition = (): ScrollData | null => {
    const currentPath = window.location.pathname;

    // Сначала проверяем sessionStorage (быстрее)
    if (useSessionStorage) {
      try {
        const sessionData = sessionStorage.getItem(SCROLL_STORAGE_KEY);
        if (sessionData) {
          const parsed = JSON.parse(sessionData) as ScrollData;
          if (parsed.path === currentPath) {
            return parsed;
          }
        }
      } catch (error) {
        console.warn('Failed to get scroll position from sessionStorage:', error);
      }
    }

    return null;
  };

  // Восстановление позиции скролла
  const restoreScrollPosition = async (scrollData: ScrollData) => {
    if (!enabled || isRestoring.value) {
      return;
    }

    isRestoring.value = true;

    // Проверяем, нужно ли восстанавливать для текущего устройства
    const shouldRestoreOnMobile = !desktopOnly && isMobile.value;
    const shouldRestoreOnDesktop = !mobileOnly && !isMobile.value;

    if (!shouldRestoreOnMobile && !shouldRestoreOnDesktop) {
      isRestoring.value = false;

      return;
    }

    // Проверяем актуальность данных (не старше 5 минут)
    const dataAge = Date.now() - scrollData.timestamp;
    if (dataAge > 5 * 60 * 1000) {
      isRestoring.value = false;

      return;
    }

    // Корректируем позицию скролла
    let targetPosition = scrollData.position;

    // Для мобильных устройств немного корректируем позицию
    if (isMobile.value) {
      if (targetPosition < threshold) {
        targetPosition = 0;
      } else {
        targetPosition = Math.max(0, targetPosition - threshold);
      }
    }

    // Ждем завершения загрузки и рендеринга
    await nextTick();

    // Ждем, если есть активная загрузка
    if (loaderStore.isFiltersLoading) {
      const unwatch = watch(
        () => loaderStore.isFiltersLoading,
        (loading) => {
          if (!loading) {
            unwatch();
            performScrollRestoration(targetPosition);
          }
        },
      );
    } else {
      performScrollRestoration(targetPosition);
    }
  };

  // Выполнение восстановления скролла
  const performScrollRestoration = (position: number) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          // Проверяем, что позиция в пределах допустимого
          const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          const safePosition = Math.min(position, maxScroll);

          window.scrollTo({
            top: safePosition,
            behavior: 'auto',
          });
        } catch (error) {
          console.error('Failed to restore scroll position:', error);
        } finally {
          isRestoring.value = false;

          // Очищаем sessionStorage после восстановления
          if (useSessionStorage) {
            sessionStorage.removeItem(SCROLL_STORAGE_KEY);
          }
        }
      });
    });
  };

  // Обработчик beforeunload
  const handleBeforeUnload = () => {
    saveScrollPosition();
  };

  // Обработчик visibilitychange
  const handleVisibilityChange = () => {
    if (document.hidden) {
      saveScrollPosition();
    }
  };

  // Обработчик скролла с debounce
  let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  const handleScroll = () => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    scrollTimeout = setTimeout(() => {
      saveScrollPosition();
    }, delay);
  };

  // Обработчик popstate для определения возврата назад
  const handlePopState = () => {
    // Небольшая задержка для гарантии, что страница загрузилась
    setTimeout(() => {
      const savedData = getSavedScrollPosition();
      if (savedData) {
        restoreScrollPosition(savedData);
      }
    }, 100);
  };

  // Инициализация
  const initialize = () => {
    if (isInitialized.value || !enabled) {
      return;
    }

    // Определяем тип навигации
    navigationType.value = detectNavigationType();

    // Настройка scrollRestoration для Safari
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Добавляем обработчики событий
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Обработка начального состояния
    if (navigationType.value === 'back_forward' || navigationType.value === 'reload') {
      // Для навигации назад/вперед и перезагрузка
      const savedData = getSavedScrollPosition();
      if (savedData) {
        restoreScrollPosition(savedData);
      }
    } else {
      // Для обычной навигации - сбрасываем скролл
      window.scrollTo({ top: 0 });
    }

    isInitialized.value = true;
  };

  // Отслеживаем изменение пути
  watch(
    () => route.path,
    (newPath, oldPath) => {
      if (newPath !== oldPath && navigationType.value !== 'back_forward') {
        // Сохраняем позицию для старой страницы
        saveScrollPosition();

        // Сбрасываем скролл для новой страницы
        window.scrollTo({ top: 0 });

        // Очищаем sessionStorage для старого пути
        if (useSessionStorage) {
          sessionStorage.removeItem(SCROLL_STORAGE_KEY);
        }
      }
    },
  );

  // Жизненный цикл
  onMounted(() => {
    // Небольшая задержка для инициализации
    setTimeout(initialize, 0);
  });

  onBeforeUnmount(() => {
    // Сохраняем позицию перед уходом со страницы
    saveScrollPosition();
  });

  onUnmounted(() => {
    // Удаляем обработчики событий
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('popstate', handlePopState);
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
      scrollTimeout = null;
    }

    // Очищаем sessionStorage
    if (useSessionStorage) {
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    }
  });

  return {
    // Состояние
    isInitialized,
    isRestoring,
    lastScrollPosition,
    navigationType,

    // Методы
    saveScrollPosition,
    restoreScrollPosition: () => {
      const savedData = getSavedScrollPosition();
      if (savedData) {
        restoreScrollPosition(savedData);
      }
    },
    getSavedScrollPosition,
  };
}
